import { DAYS_TO_LOAD } from '../../lib/date-helper'
import { resolveIntlLocale } from '../../lib/locale'
import type { DayAvailability } from './Timeslots.types'

/**
 * Generates a full range of days from today through DAYS_TO_LOAD days ahead,
 * merging real timeslots from the API response.
 * Days without API data get an empty timeSlots array (shown as unavailable).
 */
export function fillDays(
  rawAvailability: DayAvailability[],
  startIso = new Date().toISOString(),
): DayAvailability[] {
  const result: DayAvailability[] = []
  // Work with local date to avoid UTC-shift issues
  const cursor = new Date(startIso.split('T')[0] as string)

  for (let i = 0; i <= DAYS_TO_LOAD; i++) {
    const datePart = toDatePart(cursor.toISOString())
    const match = rawAvailability.find((a) => toDatePart(a.date) === datePart)
    result.push({
      date: cursor.toISOString(),
      timeSlots: match?.timeSlots ?? [],
    })
    cursor.setDate(cursor.getDate() + 1)
  }

  return result
}

export function isDayAvailable(day: DayAvailability): boolean {
  return day.timeSlots.some((s) => s.available)
}

/** Returns "YYYY-MM-DD" from any ISO string. */
export function toDatePart(iso: string): string {
  return iso.split('T')[0] ?? iso
}

/**
 * Parses the date part of an ISO string as a local calendar date.
 *
 * `new Date('2026-04-07')` is UTC midnight, which lands on the previous day for
 * anyone west of Greenwich — the day tabs would be off by one. Building the date
 * from its parts keeps it on the calendar day the API meant.
 */
function toLocalDate(isoDate: string): Date | null {
  const [year, month, day] = (isoDate.split('T')[0] ?? '')
    .split('-')
    .map((part) => Number.parseInt(part, 10))

  if (!year || !month || !day) return null

  return new Date(year, month - 1, day)
}

/**
 * One `Intl.DateTimeFormat` per locale and style.
 *
 * Constructing the formatter is the expensive part of `Intl`, and the day
 * picker formats two labels for each of its ~15 tabs on every re-render
 * (arrow nav, slide, selection).
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>()

function getFormatter(
  locale: string,
  style: 'month-day' | 'weekday',
): Intl.DateTimeFormat {
  const key = `${locale}|${style}`
  let formatter = formatterCache.get(key)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(
      locale,
      style === 'weekday'
        ? { weekday: 'short' }
        : { month: 'short', day: 'numeric' },
    )
    formatterCache.set(key, formatter)
  }

  return formatter
}

/**
 * Returns the date tab's day label — "Apr 7" in English, "7 apr" in Dutch.
 *
 * Both the month name and the part order come from the locale, so this is not
 * an English string with a translated month dropped into it.
 */
export function formatMonthDay(isoDate: string, language?: string): string {
  const date = toLocalDate(isoDate)
  if (!date) return ''

  return getFormatter(resolveIntlLocale(language), 'month-day').format(date)
}

/** Returns the date tab's weekday label — "Mon" in English, "ma" in Dutch. */
export function formatWeekday(isoDate: string, language?: string): string {
  const date = toLocalDate(isoDate)
  if (!date) return ''

  return getFormatter(resolveIntlLocale(language), 'weekday').format(date)
}

/** Returns "HH:MM" extracted from an ISO datetime string. */
export function formatTime(isoDatetime: string): string {
  const timePart = isoDatetime.split('T')[1]
  return timePart?.substring(0, 5) ?? ''
}

/**
 * Returns true if two datetime strings refer to the same calendar minute.
 * Handles mixed formats (with/without `Z`, offsets, milliseconds) by comparing
 * local Y/M/D/H/M — same approach as dayjs(..., 'minutes') in the old tool.
 */
export function isSameMinute(a: string, b: string): boolean {
  if (!a || !b) return false
  const da = new Date(a)
  const db = new Date(b)
  if (!Number.isNaN(da.getTime()) && !Number.isNaN(db.getTime())) {
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate() &&
      da.getHours() === db.getHours() &&
      da.getMinutes() === db.getMinutes()
    )
  }
  // Fallback for unparseable values: compare date + HH:MM
  return (
    `${toDatePart(a)}T${formatTime(a)}` === `${toDatePart(b)}T${formatTime(b)}`
  )
}
